// apps/web/src/features/commercial-proposals/format.ts
//
// @aiPurpose locale-aware display formatters for the commercial-proposals
//   workspace (#1198). Money values arrive either as 2dp strings (backend
//   `calculation`) or as JS numbers (the live preview); both render through
//   the same locale number formatter so the preview and the post-save
//   backend values look identical. Currency suffixes (₽ / ¥ / $) are
//   unit symbols, not translatable copy, so they stay inline here.
import { formatNumber } from '../../i18n/index.js';
import type { AppLocale } from '../../i18n/index.js';

function toNumber(value: number | string): number {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

function money(value: number | string, locale?: AppLocale): string {
  return new Intl.NumberFormat(localeTag(locale), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(value));
}

// WHY: `formatNumber` from the i18n layer already maps AppLocale → BCP-47;
//   reuse it for the quantity case and build a matching Intl tag for the
//   2dp money case via a tiny local map kept in lockstep with the locales.
function localeTag(locale?: AppLocale): string {
  if (locale === 'en') return 'en-US';
  if (locale === 'zh-Hans') return 'zh-Hans';
  return 'ru-RU';
}

/** "1 234 567,89 ₽" — rouble money. */
export function formatRub(value: number | string, locale?: AppLocale): string {
  return `${money(value, locale)} ₽`;
}

/** "68 040,00 ¥" — yuan money. */
export function formatCny(value: number | string, locale?: AppLocale): string {
  return `${money(value, locale)} ¥`;
}

/** "3 600,00 $" — dollar money. */
export function formatUsd(value: number | string, locale?: AppLocale): string {
  return `${money(value, locale)} $`;
}

/** Quantity with up to 3 fraction digits (weight, volume, rate). */
export function formatQty(value: number | string, locale?: AppLocale): string {
  return formatNumber(toNumber(value), locale);
}
